/**
 * #979 — card sources context, mirroring #978's `CardNotesProvider`.
 *
 * Mount this around a page's cards ONLY when a real adapter exists. With no
 * provider every `CardSourceControl` renders nothing, which is how the normal
 * Position Tracker route stays untouched until #976 delivers persistence:
 * no control, no read, no listener.
 *
 * The adapter is fixed for the provider's lifetime (key the provider to swap
 * it). `scope` is the resolved account scope from the integrator; changing it
 * — account/org switch, logout (null) — drops the dictionary, every
 * assignment and every in-flight write before the new scope can render.
 *
 * The provider also owns the page's ONE add-source dialog: a control asks to
 * open it and says what to do after, so two cards can never have two dialogs.
 */
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { createCardSourcesController, type CardSourcesController } from '@/lib/cardSources/cardSourcesController';
import type { CardSourceAdapter } from '@/lib/cardSources/types';
import { AddSourceDialog } from './AddSourceDialog';

/** What the opening control does once the dialog closes. */
export interface AddSourceRequest {
  /** A name was added (or an existing one reused): e.g. reopen the menu. */
  onAdded: () => void;
  /** Closed without adding: e.g. return focus to the trigger. */
  onCancelled: () => void;
}

/**
 * `scope` rides the context as well as the store: a control mounting in the
 * same commit as a switch reads the NEW scope from its props, before the
 * layout effect has reset the store, so it can never build an old-scope target.
 */
interface CardSourcesContextValue {
  controller: CardSourcesController;
  scope: string | null;
  openAddSource: (request: AddSourceRequest) => void;
}

const CardSourcesContext = createContext<CardSourcesContextValue | null>(null);

export function useCardSources(): CardSourcesContextValue | null {
  return useContext(CardSourcesContext);
}

export function CardSourcesProvider({
  adapter,
  scope,
  children,
}: {
  adapter: CardSourceAdapter;
  /** null = no resolved identity: controls render disabled with a reason. */
  scope: string | null;
  children: ReactNode;
}) {
  const [controller] = useState(() => createCardSourcesController(adapter, scope));
  // Layout effect: the reset lands before the browser paints the new scope.
  useLayoutEffect(() => controller.setScope(scope), [controller, scope]);
  // `opening` keys the dialog, so every opening starts from an empty draft.
  // It is tied to the scope generation it opened in: any account switch —
  // including A→B→A — closes it for good (the draft is dropped, as notes drop
  // theirs), so a name typed for one account is never added under another.
  const generation = useStore(controller.store, (s) => s.generation);
  const openings = useRef(0);
  const [dialog, setDialog] = useState<{ opening: number; generation: number; request: AddSourceRequest } | null>(
    null,
  );
  const live = dialog != null && dialog.generation === generation ? dialog : null;
  const openAddSource = useCallback(
    (request: AddSourceRequest) => {
      const opening = ++openings.current;
      const now = controller.store.getState().generation;
      setDialog((d) => (d && d.generation === now ? d : { opening, generation: now, request }));
    },
    [controller],
  );
  const value = useMemo(() => ({ controller, scope, openAddSource }), [controller, scope, openAddSource]);
  return (
    <CardSourcesContext.Provider value={value}>
      {children}
      {live && scope != null && (
        <AddSourceDialog
          key={live.opening}
          controller={controller}
          onClose={(added) => {
            // Close THIS dialog only: a late answer from an older one must not
            // close a newer dialog (or lose its draft).
            setDialog((d) => (d?.opening === live.opening ? null : d));
            // After an account switch the opener belongs to the old account:
            // don't reopen its menu or pull focus back to it.
            if (live.generation !== controller.store.getState().generation) return;
            if (added) live.request.onAdded();
            else live.request.onCancelled();
          }}
        />
      )}
    </CardSourcesContext.Provider>
  );
}
